import React, { useState, useMemo } from 'react';
import { StructuredParagraph, ParagraphCitation, CitationAnchor, CitationValidationDefect } from '../types/api';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { CitationSourcePanel } from './CitationSourcePanel';

interface StructuredAnswerProps {
  paragraphs: StructuredParagraph[];
  citations?: CitationAnchor[];
  opinions?: { id: string; polarity?: string }[];
}

/**
 * Renders structured paragraphs with expandable citation details.
 * Each paragraph shows its text with markdown support and a citation count badge.
 * Expanding reveals evidence quotes, confidence, and relevance for each citation.
 */
export const StructuredAnswer: React.FC<StructuredAnswerProps> = ({
  paragraphs,
  citations,
  opinions
}) => {
  const [expandedParagraphs, setExpandedParagraphs] = useState<Set<number>>(new Set());
  const [selectedCitation, setSelectedCitation] = useState<CitationAnchor | null>(null);
  const [selectedFullTextStartOffset, setSelectedFullTextStartOffset] = useState<number | undefined>();
  const [selectedFullTextEndOffset, setSelectedFullTextEndOffset] = useState<number | undefined>();

  const toggleParagraph = (index: number) => {
    setExpandedParagraphs(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Group citations by document ID + chunk text (same pattern as AnswerWithCitations)
  const citationsBySource = useMemo(() => {
    if (!citations) return new Map<string, CitationAnchor[]>();
    const grouped = new Map<string, CitationAnchor[]>();
    citations.forEach((citation, index) => {
      const key = `${citation.documentId}:${citation.chunkText}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push({ ...citation, citationIndex: index + 1 } as any);
    });
    return grouped;
  }, [citations]);

  const selectedSourceCitations = useMemo(() => {
    if (!selectedCitation) return [];
    const key = `${selectedCitation.documentId}:${selectedCitation.chunkText}`;
    return citationsBySource.get(key) || [];
  }, [selectedCitation, citationsBySource]);

  const handleCitationClick = (documentId: string, evidenceQuote?: string) => {
    if (!citations || !paragraphs) return;
    let match: CitationAnchor | undefined;
    let fullTextStartOffset: number | undefined;
    let fullTextEndOffset: number | undefined;

    // Find the ParagraphCitation to get full-text offsets
    for (const para of paragraphs) {
      const paraCitation = para.citations.find(c => 
        c.documentId === documentId && 
        (c.evidenceQuote === evidenceQuote || 
         (evidenceQuote && c.evidenceQuote.slice(0, 60) === evidenceQuote.slice(0, 60)))
      );
      if (paraCitation) {
        fullTextStartOffset = paraCitation.fullTextStartOffset;
        fullTextEndOffset = paraCitation.fullTextEndOffset;
        break;
      }
    }

    if (evidenceQuote) {
      // Match against anchorText first (exact)
      match = citations.find(c => c.documentId === documentId && c.anchorText === evidenceQuote);
      if (!match) {
        // Match against the actual cited span text (substring at offsets)
        match = citations.find(c => {
          if (c.documentId !== documentId) return false;
          const span = c.chunkText.substring(c.startOffset, c.endOffset);
          return span.includes(evidenceQuote.slice(0, 60)) || evidenceQuote.includes(span.slice(0, 60));
        });
      }
    }
    if (!match) {
      match = citations.find(c => c.documentId === documentId);
    }
    if (match) {
      setSelectedCitation(match);
      setSelectedFullTextStartOffset(fullTextStartOffset);
      setSelectedFullTextEndOffset(fullTextEndOffset);
    }
  };

  return (
    <div data-component="StructuredAnswer" className="structured-answer space-y-4" style={{ position: 'relative' }}>
      {paragraphs.map((paragraph, idx) => (
        <ParagraphBlock
          key={idx}
          paragraph={paragraph}
          index={idx}
          isExpanded={expandedParagraphs.has(idx)}
          onToggle={() => toggleParagraph(idx)}
          opinions={opinions}
          onCitationClick={citations ? handleCitationClick : undefined}
        />
      ))}

      {selectedCitation && (
        <CitationSourcePanel
          citations={selectedSourceCitations}
          polarity={opinions?.find(o => o.id === selectedCitation.documentId)?.polarity}
          onClose={() => setSelectedCitation(null)}
          targetStartOffset={selectedCitation.startOffset}
          fullTextStartOffset={selectedFullTextStartOffset}
          fullTextEndOffset={selectedFullTextEndOffset}
        />
      )}
    </div>
  );
};

interface ParagraphBlockProps {
  paragraph: StructuredParagraph;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  opinions?: { id: string; polarity?: string }[];
  onCitationClick?: (documentId: string, evidenceQuote?: string) => void;
}

const ParagraphBlock: React.FC<ParagraphBlockProps> = ({
  paragraph,
  index,
  isExpanded,
  onToggle,
  opinions,
  onCitationClick
}) => {
  const citationCount = paragraph.citations.length;

  return (
    <div data-flow={`paragraph-${index}`} className="paragraph-block">
      <div className="prose prose-sm max-w-none">
        {parseMarkdown(paragraph.text)}
      </div>

      {citationCount > 0 && (
        <div className="mt-2">
          <button
            onClick={onToggle}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer border-none"
          >
            {citationCount} citation{citationCount > 1 ? 's' : ''}
            {isExpanded
              ? <ChevronUp className="w-3 h-3" />
              : <ChevronDown className="w-3 h-3" />
            }
          </button>

          {isExpanded && (
            <div className="mt-2 space-y-3 pl-3 border-l-2 border-blue-200">
              {paragraph.citations.map((citation, cIdx) => (
                <CitationDetail
                  key={cIdx}
                  citation={citation}
                  polarity={opinions?.find(o => o.id === citation.documentId)?.polarity}
                  onCitationClick={onCitationClick}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface CitationDetailProps {
  citation: ParagraphCitation;
  polarity?: string;
  onCitationClick?: (documentId: string, evidenceQuote?: string) => void;
}

const CitationDetail: React.FC<CitationDetailProps> = ({ citation, polarity, onCitationClick }) => {
  const handleClick = () => {
    if (onCitationClick) {
      onCitationClick(citation.documentId, citation.evidenceQuote);
    }
  };

  const validationStatus = getValidationStatus(citation.validationDefects);
  const hasFullTextOffset = citation.fullTextStartOffset !== undefined;

  return (
    <div
      className={`text-sm space-y-1 ${onCitationClick ? 'cursor-pointer rounded p-1.5 -m-1.5 hover:bg-blue-50 transition-colors' : ''}`}
      onClick={handleClick}
      role={onCitationClick ? 'button' : undefined}
      tabIndex={onCitationClick ? 0 : undefined}
      onKeyDown={onCitationClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); } : undefined}
    >
      <blockquote className={`border-l-2 border-gray-300 pl-3 italic text-gray-600 text-xs my-1 ${validationStatus === 'invalid' ? 'opacity-60 line-through' : ''}`}>
        {citation.evidenceQuote}
      </blockquote>

      <div className="flex items-center gap-2 flex-wrap">
        <ConfidenceBadge label="Confidence" value={citation.confidence} />
        <ConfidenceBadge label="Relevance" value={citation.relevance} />
        <span className="text-xs text-gray-500">
          Doc: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">{citation.documentId}</code>
        </span>
        {polarity && <PolarityBadge polarity={polarity} />}
        <ValidationBadge defects={citation.validationDefects} />
      </div>

      {validationStatus === 'warning' && citation.validationDefects && citation.validationDefects.length > 0 && (
        <div className="text-xs italic text-amber-700 mt-1">
          {citation.validationDefects.map((d, i) => (
            <div key={i}>{d.description}</div>
          ))}
        </div>
      )}

      {validationStatus === 'validated' && hasFullTextOffset && (
        <div className="text-xs mt-1 text-emerald-700">
          ✓ Located in full text
        </div>
      )}
    </div>
  );
};

function getScoreColor(value: number): string {
  if (value >= 0.8) return 'bg-green-100 text-green-800';
  if (value >= 0.5) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
}

const ConfidenceBadge: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  const colorClass = getScoreColor(value);
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {label}: {Math.round(value * 100)}%
    </span>
  );
};

const PolarityBadge: React.FC<{ polarity: string }> = ({ polarity }) => {
  const colorClass =
    polarity === 'contrary' ? 'bg-red-100 text-red-800' :
    polarity === 'mixed' ? 'bg-yellow-100 text-yellow-800' :
    polarity === 'supporting' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {polarity.charAt(0).toUpperCase() + polarity.slice(1)}
    </span>
  );
};

const HARD_DEFECT_TYPES = new Set(['ExactMatchFailed', 'Unsupported', 'Contradicted']);

type ValidationStatus = 'validated' | 'invalid' | 'warning' | null;

function getValidationStatus(defects?: CitationValidationDefect[]): ValidationStatus {
  if (defects === undefined || defects === null) return null;
  if (defects.length === 0) return 'validated';
  if (defects.some(d => HARD_DEFECT_TYPES.has(d.defectType))) return 'invalid';
  return 'warning';
}

const ValidationBadge: React.FC<{ defects?: CitationValidationDefect[] }> = ({ defects }) => {
  const status = getValidationStatus(defects);
  if (!status) return null;

  const tooltip = defects && defects.length > 0
    ? defects.map(d => `[${d.layer}] ${d.defectType}: ${d.description}`).join('\n')
    : 'Citation verified against source text';

  if (status === 'validated') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800" title={tooltip}>
        ✓ Verified
      </span>
    );
  }

  if (status === 'invalid') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" title={tooltip}>
        ✗ Invalid
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800" title={tooltip}>
      ⚠ Warning
    </span>
  );
};

// --- Markdown parsing (reused pattern from CitationHighlight) ---

function parseMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let key = 0;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (/^\s*[*-]\s+/.test(line)) {
      const listItems: React.ReactNode[] = [];
      let listKey = 0;
      while (i < lines.length && /^\s*[*-]\s+/.test(lines[i])) {
        const content = lines[i].replace(/^\s*[*-]\s+/, '');
        listItems.push(<li key={listKey++} className="mb-1">{parseInlineMarkdown(content)}</li>);
        i++;
      }
      elements.push(<ul key={key++} className="list-disc ml-6 my-2">{listItems}</ul>);
      continue;
    } else if (/^\s*\d+\.\s+/.test(line)) {
      const listItems: React.ReactNode[] = [];
      let listKey = 0;
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        const content = lines[i].replace(/^\s*\d+\.\s+/, '');
        listItems.push(<li key={listKey++} className="mb-1">{parseInlineMarkdown(content)}</li>);
        i++;
      }
      elements.push(<ol key={key++} className="list-decimal ml-6 my-2">{listItems}</ol>);
      continue;
    } else if (line.trim() === '') {
      elements.push(<br key={key++} />);
    } else {
      elements.push(
        <span key={key++}>
          {parseInlineMarkdown(line)}
          {i < lines.length - 1 && <br />}
        </span>
      );
    }
    i++;
  }

  return elements;
}

function parseInlineMarkdown(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
    if (boldMatch) {
      parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }

    const italicMatch = remaining.match(/^\*([^*]+?)\*/);
    if (italicMatch) {
      parts.push(<em key={key++}>{italicMatch[1]}</em>);
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }

    const codeMatch = remaining.match(/^`(.+?)`/);
    if (codeMatch) {
      parts.push(
        <code key={key++} className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">
          {codeMatch[1]}
        </code>
      );
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }

    parts.push(remaining[0]);
    remaining = remaining.slice(1);
  }

  return parts;
}
